import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, Cell } from 'recharts';
const SafetyIncidentsDashboard: React.FC = () => {
  const kpis = [{
    title: 'LTIFR',
    value: '0.42',
    change: '-0.15',
    isPositive: true,
    subtitle: 'Lost Time Injury Frequency Rate'
  }, {
    title: 'Safety Observations',
    value: '1,248',
    change: '+185',
    isPositive: true,
    subtitle: 'Proactive reporting'
  }, {
    title: 'Incident Severity %',
    value: '8.5%',
    change: '-2.1%',
    isPositive: true,
    subtitle: 'High severity incidents'
  }];
  // Incident Trend Data
  const incidentTrendData = [{
    month: 'Jan',
    fatal: 0,
    lostTime: 2,
    medicalTreatment: 5,
    firstAid: 12,
    nearMiss: 28
  }, {
    month: 'Feb',
    fatal: 0,
    lostTime: 1,
    medicalTreatment: 4,
    firstAid: 10,
    nearMiss: 32
  }, {
    month: 'Mar',
    fatal: 0,
    lostTime: 2,
    medicalTreatment: 6,
    firstAid: 14,
    nearMiss: 35
  }, {
    month: 'Apr',
    fatal: 0,
    lostTime: 1,
    medicalTreatment: 3,
    firstAid: 9,
    nearMiss: 38
  }, {
    month: 'May',
    fatal: 0,
    lostTime: 1,
    medicalTreatment: 4,
    firstAid: 11,
    nearMiss: 42
  }, {
    month: 'Jun',
    fatal: 0,
    lostTime: 0,
    medicalTreatment: 3,
    firstAid: 8,
    nearMiss: 45
  }];
  // Severity Pyramid Data
  const severityPyramidData = [{
    category: 'Fatal',
    count: 0,
    color: '#7f1d1d'
  }, {
    category: 'Lost Time',
    count: 7,
    color: '#ef4444'
  }, {
    category: 'Medical Treatment',
    count: 25,
    color: '#f97316'
  }, {
    category: 'First Aid',
    count: 64,
    color: '#fbbf24'
  }, {
    category: 'Near Miss',
    count: 220,
    color: '#3b82f6'
  }];
  // Unsafe Act Heatmap Data
  const unsafeActHeatmapData = [{
    plant: 'Manesar',
    'PPE Non-compliance': 18,
    'Unsafe Method': 12,
    Housekeeping: 8,
    'Equipment Misuse': 6
  }, {
    plant: 'Chennai',
    'PPE Non-compliance': 14,
    'Unsafe Method': 10,
    Housekeeping: 6,
    'Equipment Misuse': 4
  }, {
    plant: 'Pune',
    'PPE Non-compliance': 22,
    'Unsafe Method': 15,
    Housekeeping: 10,
    'Equipment Misuse': 8
  }, {
    plant: 'Bangalore',
    'PPE Non-compliance': 16,
    'Unsafe Method': 11,
    Housekeeping: 7,
    'Equipment Misuse': 5
  }];
  // Incident Log Data
  const incidentLogData = [{
    incidentId: 'SI-2024-001',
    date: '2024-01-15',
    time: '14:30',
    plant: 'Manesar',
    area: 'Production Floor - Line 3',
    incidentType: 'Lost Time Injury',
    severity: 'High',
    description: 'Operator suffered hand laceration while operating press machine',
    injuredPerson: 'Rajesh Kumar',
    bodyPart: 'Left Hand',
    daysLost: 5,
    rootCause: 'Machine guard was bypassed for faster operation',
    immediateAction: 'First aid provided, employee sent to hospital for stitches',
    correctiveAction: 'Machine guard interlock system installed, cannot operate without guard',
    preventiveAction: 'All press machines being retrofitted with interlocks, safety training reinforced',
    investigatedBy: 'Safety Officer',
    status: 'Closed',
    closureDate: '2024-02-01',
    remarks: 'Employee returned to work after 5 days, no permanent disability'
  }, {
    incidentId: 'SI-2024-002',
    date: '2024-01-18',
    time: '09:15',
    plant: 'Chennai',
    area: 'Warehouse',
    incidentType: 'First Aid',
    severity: 'Low',
    description: 'Minor cut on finger while handling packaging materials',
    injuredPerson: 'Suresh Menon',
    bodyPart: 'Right Index Finger',
    daysLost: 0,
    rootCause: 'Sharp edge on damaged packaging box',
    immediateAction: 'First aid administered on-site, bandage applied',
    correctiveAction: 'Damaged boxes removed from circulation',
    preventiveAction: 'Incoming packaging inspection checklist updated',
    investigatedBy: 'Warehouse Supervisor',
    status: 'Closed',
    closureDate: '2024-01-20',
    remarks: 'Minor incident, no time lost'
  }, {
    incidentId: 'SI-2024-003',
    date: '2024-01-22',
    time: '16:45',
    plant: 'Pune',
    area: 'Maintenance Shop',
    incidentType: 'Medical Treatment',
    severity: 'Medium',
    description: 'Chemical splash on face during maintenance activity',
    injuredPerson: 'Amit Patil',
    bodyPart: 'Face/Eyes',
    daysLost: 2,
    rootCause: 'Face shield not worn during chemical handling',
    immediateAction: 'Emergency eyewash used immediately, sent to medical facility',
    correctiveAction: 'Mandatory PPE compliance enforced with supervision',
    preventiveAction: 'PPE compliance audit scheduled weekly, disciplinary policy reinforced',
    investigatedBy: 'Safety Team',
    status: 'Closed',
    closureDate: '2024-02-05',
    remarks: 'No permanent eye damage, employee fully recovered'
  }, {
    incidentId: 'SI-2024-004',
    date: '2024-01-25',
    time: '11:20',
    plant: 'Bangalore',
    area: 'Loading Bay',
    incidentType: 'Near Miss',
    severity: 'Medium',
    description: 'Forklift nearly struck pedestrian in loading area',
    injuredPerson: 'N/A',
    bodyPart: 'N/A',
    daysLost: 0,
    rootCause: 'Pedestrian walkway not clearly marked, forklift operator visibility issue',
    immediateAction: 'Operations temporarily halted, area inspected',
    correctiveAction: 'Bright yellow pedestrian walkway markings applied',
    preventiveAction: 'Proximity sensors being installed on all forklifts, additional safety signage',
    investigatedBy: 'Safety Officer',
    status: 'Closed',
    closureDate: '2024-02-08',
    remarks: 'Serious near miss, preventive measures critical'
  }, {
    incidentId: 'SI-2024-005',
    date: '2024-02-01',
    time: '13:00',
    plant: 'Manesar',
    area: 'Quality Lab',
    incidentType: 'First Aid',
    severity: 'Low',
    description: 'Employee slipped on wet floor, minor bruise on knee',
    injuredPerson: 'Priya Sharma',
    bodyPart: 'Right Knee',
    daysLost: 0,
    rootCause: 'Wet floor after cleaning, warning sign not placed',
    immediateAction: 'First aid provided, ice pack applied',
    correctiveAction: 'Cleaning procedure updated to include mandatory signage',
    preventiveAction: 'Anti-slip mats placed in high-traffic areas, cleaning schedule adjusted',
    investigatedBy: 'Lab Manager',
    status: 'Closed',
    closureDate: '2024-02-03',
    remarks: 'Minor incident, employee continued work same day'
  }, {
    incidentId: 'SI-2024-006',
    date: '2024-02-03',
    time: '10:30',
    plant: 'Chennai',
    area: 'Production Floor - Line 1',
    incidentType: 'Lost Time Injury',
    severity: 'High',
    description: 'Back strain while manually lifting heavy component',
    injuredPerson: 'Venkat Raman',
    bodyPart: 'Lower Back',
    daysLost: 8,
    rootCause: 'Lifting aid not used, improper lifting technique',
    immediateAction: 'Employee taken to medical facility, prescribed rest',
    correctiveAction: 'Mechanical lifting aids provided at all heavy lifting stations',
    preventiveAction: 'Ergonomics training conducted, weight limits clearly posted',
    investigatedBy: 'Safety Officer',
    status: 'Closed',
    closureDate: '2024-02-20',
    remarks: 'Employee recovered fully, returned to light duties initially'
  }, {
    incidentId: 'SI-2024-007',
    date: '2024-02-05',
    time: '15:45',
    plant: 'Pune',
    area: 'Electrical Room',
    incidentType: 'Near Miss',
    severity: 'High',
    description: 'Electrical panel door found open with live circuits exposed',
    injuredPerson: 'N/A',
    bodyPart: 'N/A',
    daysLost: 0,
    rootCause: 'Maintenance work not completed properly, lockout-tagout not followed',
    immediateAction: 'Area cordoned off immediately, panel secured by electrician',
    correctiveAction: 'LOTO procedure violation investigation, disciplinary action taken',
    preventiveAction: 'LOTO refresher training for all maintenance staff, audit system enhanced',
    investigatedBy: 'Electrical Supervisor',
    status: 'Closed',
    closureDate: '2024-02-12',
    remarks: 'Critical safety violation, could have been fatal'
  }, {
    incidentId: 'SI-2024-008',
    date: '2024-02-08',
    time: '08:30',
    plant: 'Bangalore',
    area: 'Chemical Storage',
    incidentType: 'Near Miss',
    severity: 'High',
    description: 'Chemical container found leaking, spill contained before exposure',
    injuredPerson: 'N/A',
    bodyPart: 'N/A',
    daysLost: 0,
    rootCause: 'Container damage during handling, inspection not performed',
    immediateAction: 'Spill contained using absorbent materials, area ventilated',
    correctiveAction: 'All chemical containers inspected, damaged ones replaced',
    preventiveAction: 'Daily chemical storage inspection checklist implemented',
    investigatedBy: 'Safety Team',
    status: 'Closed',
    closureDate: '2024-02-15',
    remarks: 'Timely detection prevented potential chemical exposure'
  }, {
    incidentId: 'SI-2024-009',
    date: '2024-02-10',
    time: '12:00',
    plant: 'Manesar',
    area: 'Canteen',
    incidentType: 'First Aid',
    severity: 'Low',
    description: 'Minor burn from hot beverage spill',
    injuredPerson: 'Anita Desai',
    bodyPart: 'Left Hand',
    daysLost: 0,
    rootCause: 'Accidental spill while carrying hot coffee',
    immediateAction: 'Cold water applied immediately, burn ointment and bandage',
    correctiveAction: 'Lids made mandatory for all hot beverages',
    preventiveAction: 'Spill-proof containers being procured for canteen',
    investigatedBy: 'Canteen Supervisor',
    status: 'Closed',
    closureDate: '2024-02-11',
    remarks: 'Very minor burn, first-degree only'
  }, {
    incidentId: 'SI-2024-010',
    date: '2024-02-12',
    time: '14:15',
    plant: 'Chennai',
    area: 'Production Floor - Line 2',
    incidentType: 'Medical Treatment',
    severity: 'Medium',
    description: 'Foreign object in eye during grinding operation',
    injuredPerson: 'Karthik Subramanian',
    bodyPart: 'Right Eye',
    daysLost: 1,
    rootCause: 'Safety glasses not worn properly, gap allowed particle entry',
    immediateAction: 'Emergency eyewash used, sent to medical facility for examination',
    correctiveAction: 'Proper safety glasses fitting training conducted',
    preventiveAction: 'Safety glasses inspection added to daily safety checklist',
    investigatedBy: 'Production Supervisor',
    status: 'In Progress',
    closureDate: '',
    remarks: 'Particle removed at medical facility, monitoring for infection'
  }];
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
      case 'High':
        return 'bg-red-100 text-red-700';
      case 'Medium':
        return 'bg-orange-100 text-orange-700';
      case 'Low':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getIncidentTypeColor = (type: string) => {
    switch (type) {
      case 'Fatal':
        return 'bg-red-900 text-white';
      case 'Lost Time Injury':
        return 'bg-red-100 text-red-700';
      case 'Medical Treatment':
        return 'bg-orange-100 text-orange-700';
      case 'First Aid':
        return 'bg-yellow-100 text-yellow-700';
      case 'Near Miss':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  return <div data-id="element-2461">
      <DescriptionPanel title="Safety Incidents & Near-Misses" description="Comprehensive safety incident tracking system monitoring workplace injuries, near-misses, and unsafe conditions. Track LTIFR, analyze incident trends, and implement corrective actions to maintain a safe working environment across all facilities." insights={['LTIFR improved to 0.42 - 26% reduction from previous period', 'Safety observations increased to 1,248 - strong proactive safety culture', 'High severity incidents reduced to 8.5% - down from 10.6% last quarter', 'Near-miss reporting up 45% - improved safety awareness and reporting']} data-id="element-2462" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-2463">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-2464">
          Safety Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2465">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-2466">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-2467">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-2468">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-2469">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-2470">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-2471" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-2472" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Incident Trend and Severity Pyramid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-2473">
        {/* Incident Trend */}
        <Card className="p-6" data-id="element-2474">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2475">
            Incident Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2476">
            Monthly trend of incidents by severity level
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2477">
            <LineChart data={incidentTrendData} data-id="element-2478">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2479" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-2480" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-2481" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-2482" />
              <Legend data-id="element-2483" />
              <Line type="monotone" dataKey="fatal" stroke="#7f1d1d" strokeWidth={3} name="Fatal" dot={{
              r: 5
            }} data-id="element-2484" />
              <Line type="monotone" dataKey="lostTime" stroke="#ef4444" strokeWidth={3} name="Lost Time" dot={{
              r: 5
            }} data-id="element-2485" />
              <Line type="monotone" dataKey="medicalTreatment" stroke="#f97316" strokeWidth={3} name="Medical Treatment" dot={{
              r: 5
            }} data-id="element-2486" />
              <Line type="monotone" dataKey="firstAid" stroke="#fbbf24" strokeWidth={3} name="First Aid" dot={{
              r: 5
            }} data-id="element-2487" />
              <Line type="monotone" dataKey="nearMiss" stroke="#3b82f6" strokeWidth={3} name="Near Miss" dot={{
              r: 5
            }} data-id="element-2488" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2489">
            <div className="grid grid-cols-5 gap-2 text-xs" data-id="element-2490">
              <div className="text-center" data-id="element-2491">
                <div className="text-gray-600" data-id="element-2492">Fatal</div>
                <div className="text-sm font-bold text-red-900" data-id="element-2493">0</div>
              </div>
              <div className="text-center" data-id="element-2494">
                <div className="text-gray-600" data-id="element-2495">Lost Time</div>
                <div className="text-sm font-bold text-red-600" data-id="element-2496">0</div>
                <div className="text-xs text-gray-500" data-id="element-2497">this month</div>
              </div>
              <div className="text-center" data-id="element-2498">
                <div className="text-gray-600" data-id="element-2499">Medical</div>
                <div className="text-sm font-bold text-orange-600" data-id="element-2500">3</div>
                <div className="text-xs text-gray-500" data-id="element-2501">this month</div>
              </div>
              <div className="text-center" data-id="element-2502">
                <div className="text-gray-600" data-id="element-2503">First Aid</div>
                <div className="text-sm font-bold text-yellow-600" data-id="element-2504">8</div>
                <div className="text-xs text-gray-500" data-id="element-2505">this month</div>
              </div>
              <div className="text-center" data-id="element-2506">
                <div className="text-gray-600" data-id="element-2507">Near Miss</div>
                <div className="text-sm font-bold text-blue-600" data-id="element-2508">45</div>
                <div className="text-xs text-gray-500" data-id="element-2509">this month</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Severity Pyramid */}
        <Card className="p-6" data-id="element-2510">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2511">
            Severity Pyramid
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-2512">
            Heinrich Safety Pyramid - incident distribution by severity
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-2513">
            <BarChart data={severityPyramidData} layout="vertical" margin={{
            left: 80
          }} data-id="element-2514">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2515" />
              <XAxis type="number" tick={{
              fontSize: 12
            }} data-id="element-2516" />
              <YAxis dataKey="category" type="category" tick={{
              fontSize: 12
            }} width={100} data-id="element-2517" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-2518" />
              <Bar dataKey="count" name="Count" data-id="element-2519">
                {severityPyramidData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2520" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2521">
            <div className="space-y-2 text-sm" data-id="element-2522">
              <div className="flex items-center justify-between" data-id="element-2523">
                <div className="flex items-center" data-id="element-2524">
                  <div className="w-4 h-4 bg-red-900 rounded mr-2" data-id="element-2525"></div>
                  <span className="text-gray-700" data-id="element-2526">Fatal</span>
                </div>
                <span className="font-bold text-gray-900" data-id="element-2527">0</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-2528">
                <div className="flex items-center" data-id="element-2529">
                  <div className="w-4 h-4 bg-red-500 rounded mr-2" data-id="element-2530"></div>
                  <span className="text-gray-700" data-id="element-2531">Lost Time Injury</span>
                </div>
                <span className="font-bold text-gray-900" data-id="element-2532">7</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-2533">
                <div className="flex items-center" data-id="element-2534">
                  <div className="w-4 h-4 bg-orange-500 rounded mr-2" data-id="element-2535"></div>
                  <span className="text-gray-700" data-id="element-2536">Medical Treatment</span>
                </div>
                <span className="font-bold text-gray-900" data-id="element-2537">25</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-2538">
                <div className="flex items-center" data-id="element-2539">
                  <div className="w-4 h-4 bg-yellow-400 rounded mr-2" data-id="element-2540"></div>
                  <span className="text-gray-700" data-id="element-2541">First Aid</span>
                </div>
                <span className="font-bold text-gray-900" data-id="element-2542">64</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-2543">
                <div className="flex items-center" data-id="element-2544">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-2" data-id="element-2545"></div>
                  <span className="text-gray-700" data-id="element-2546">Near Miss</span>
                </div>
                <span className="font-bold text-gray-900" data-id="element-2547">220</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Unsafe Act Heatmap */}
      <Card className="p-6 mb-8" data-id="element-2548">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-2549">
          Unsafe Act Heatmap
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-2550">
          Plant-wise distribution of unsafe acts and conditions by category
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-2551">
          <BarChart data={unsafeActHeatmapData} data-id="element-2552">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-2553" />
            <XAxis dataKey="plant" tick={{
            fontSize: 12
          }} data-id="element-2554" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-2555" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-2556" />
            <Legend data-id="element-2557" />
            <Bar dataKey="PPE Non-compliance" stackId="a" fill="#ef4444" name="PPE Non-compliance" data-id="element-2558" />
            <Bar dataKey="Unsafe Method" stackId="a" fill="#f97316" name="Unsafe Method" data-id="element-2559" />
            <Bar dataKey="Housekeeping" stackId="a" fill="#fbbf24" name="Housekeeping" data-id="element-2560" />
            <Bar dataKey="Equipment Misuse" stackId="a" fill="#3b82f6" name="Equipment Misuse" data-id="element-2561" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-2562">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-2563">
            <div className="text-center" data-id="element-2564">
              <div className="text-gray-600" data-id="element-2565">Total Observations</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-2566">194</div>
              <div className="text-xs text-gray-500" data-id="element-2567">across all plants</div>
            </div>
            <div className="text-center" data-id="element-2568">
              <div className="text-gray-600" data-id="element-2569">PPE Non-compliance</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2570">70</div>
              <div className="text-xs text-gray-500" data-id="element-2571">36.1% of total</div>
            </div>
            <div className="text-center" data-id="element-2572">
              <div className="text-gray-600" data-id="element-2573">Unsafe Method</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-2574">48</div>
              <div className="text-xs text-gray-500" data-id="element-2575">24.7% of total</div>
            </div>
            <div className="text-center" data-id="element-2576">
              <div className="text-gray-600" data-id="element-2577">Housekeeping</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-2578">31</div>
              <div className="text-xs text-gray-500" data-id="element-2579">16.0% of total</div>
            </div>
            <div className="text-center" data-id="element-2580">
              <div className="text-gray-600" data-id="element-2581">Equipment Misuse</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2582">23</div>
              <div className="text-xs text-gray-500" data-id="element-2583">11.9% of total</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Incident Log */}
      <Card className="p-6" data-id="element-2584">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2585">
          Incident Log
        </h3>
        <div className="overflow-x-auto" data-id="element-2586">
          <table className="w-full" data-id="element-2587">
            <thead data-id="element-2588">
              <tr className="border-b-2 border-gray-300" data-id="element-2589">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2590">
                  Incident ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2591">
                  Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2592">
                  Time
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2593">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2594">
                  Area
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2595">
                  Incident Type
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2596">
                  Severity
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2597">
                  Description
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2598">
                  Injured Person
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2599">
                  Body Part
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2600">
                  Days Lost
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2601">
                  Root Cause
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2602">
                  Immediate Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2603">
                  Corrective Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2604">
                  Preventive Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2605">
                  Investigated By
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2606">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2607">
                  Closure Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-2608">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-2609">
              {incidentLogData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-2610">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-2611">
                    {row.incidentId}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2612">
                    {row.date}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2613">
                    {row.time}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-2614">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2615">
                    {row.area}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2616">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getIncidentTypeColor(row.incidentType)}`} data-id="element-2617">
                      {row.incidentType}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2618">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getSeverityColor(row.severity)}`} data-id="element-2619">
                      {row.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2620">
                    {row.description}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2621">
                    {row.injuredPerson}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2622">
                    {row.bodyPart}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2623">
                    <span className={`text-sm font-bold ${row.daysLost > 0 ? 'text-red-600' : 'text-green-600'}`} data-id="element-2624">
                      {row.daysLost}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2625">
                    {row.rootCause}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2626">
                    {row.immediateAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2627">
                    {row.correctiveAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2628">
                    {row.preventiveAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2629">
                    {row.investigatedBy}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-2630">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-2631">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-2632">
                    {row.closureDate || '-'}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-2633">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-2634">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-2635">
            <div className="text-center" data-id="element-2636">
              <div className="text-gray-600" data-id="element-2637">Total Incidents</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-2638">10</div>
              <div className="text-xs text-gray-500" data-id="element-2639">YTD</div>
            </div>
            <div className="text-center" data-id="element-2640">
              <div className="text-gray-600" data-id="element-2641">Lost Time</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2642">2</div>
              <div className="text-xs text-gray-500" data-id="element-2643">injuries</div>
            </div>
            <div className="text-center" data-id="element-2644">
              <div className="text-gray-600" data-id="element-2645">Medical Treatment</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-2646">2</div>
              <div className="text-xs text-gray-500" data-id="element-2647">cases</div>
            </div>
            <div className="text-center" data-id="element-2648">
              <div className="text-gray-600" data-id="element-2649">First Aid</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-2650">3</div>
              <div className="text-xs text-gray-500" data-id="element-2651">minor</div>
            </div>
            <div className="text-center" data-id="element-2652">
              <div className="text-gray-600" data-id="element-2653">Near Miss</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-2654">3</div>
              <div className="text-xs text-gray-500" data-id="element-2655">prevented</div>
            </div>
            <div className="text-center" data-id="element-2656">
              <div className="text-gray-600" data-id="element-2657">Total Days Lost</div>
              <div className="text-xl font-bold text-red-600" data-id="element-2658">16</div>
              <div className="text-xs text-gray-500" data-id="element-2659">work days</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default SafetyIncidentsDashboard;